import { AfterViewInit, ChangeDetectorRef, Component, computed, effect, ElementRef, inject, OnDestroy, signal, ViewEncapsulation } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { ViewStateTypes } from '@common/enums/viewstate-types';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { LanguageSettings } from '@common/services/language-settings';
import { NavigationService } from '@common/services/navigation-service';
import { Store } from '@ngrx/store';
import { CARD_WIDTH_LOWER_BREAKPOINTS } from '@src/suite/common/enums/constants';
import { CardViewType } from '@src/suite/common/enums/enums';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { ViewStateService } from '@src/suite/common/services/view-state-service';
import { ViewStateActions, ViewStateApiActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { selectCurrentViewState } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { initializeRecord } from '@src/suite/common/utils/record-utils';
import { isSingleViewStateRecord } from '@src/suite/common/utils/viewstate-utils';
import { Util } from '@suite/common/utils/util';

import { StTopicItemComponent } from '../../../common/components/topic-item/st-topic-item.component';
import { ComboBoxComponent } from '../../../common/controls/combo-box/combo-box';

@Component({
    selector: 'st-topic-custom-fields',
    imports: [ComboBoxComponent, MatButton, StTopicItemComponent],
    templateUrl: './topic-custom-fields.html',
    styleUrl: './topic-custom-fields.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-topic-custom-fields',
    },
})
export class TopicCustomFieldsComponent implements AfterViewInit, OnDestroy {
    globalTablesService = inject(GlobalTablesService);
    viewStateService = inject(ViewStateService);
    languageSettings = inject(LanguageSettings);
    dialogRef = inject<MatDialogRef<TopicCustomFieldsComponent>>(MatDialogRef);
    navigationService = inject(NavigationService);
    changeDetectorRef = inject(ChangeDetectorRef);
    nomWebServicesJson = inject(NomWebServicesJson);

    private store = inject(Store);
    private elmRef = inject(ElementRef);

    public readonly data = signal(initializeRecord<StorytellingTopicRecord>(ObjectTypes.TOPIC));
    readonly cardViewType = signal(CardViewType.LARGE);
    private containerResizeObserver: ResizeObserver;

    readonly vs = computed(() => this.store.selectSignal(selectCurrentViewState)());
    readonly viewStateCustomCardItems = signal<FieldDef[]>([]);

    readonly customFieldValue0 = computed(() => {
        return {
            id: this.viewStateCustomCardItems()[0]?.ID,
            title: this.viewStateCustomCardItems()[0]?.DefaultCaption,
        };
    });

    readonly customFieldValue1 = computed(() => {
        return {
            id: this.viewStateCustomCardItems()[1]?.ID,
            title: this.viewStateCustomCardItems()[1]?.DefaultCaption,
        };
    });

    readonly customFieldValue2 = computed(() => {
        return {
            id: this.viewStateCustomCardItems()[2]?.ID,
            title: this.viewStateCustomCardItems()[2]?.DefaultCaption,
        };
    });

    readonly customFieldValue3 = computed(() => {
        return {
            id: this.viewStateCustomCardItems()[3]?.ID,
            title: this.viewStateCustomCardItems()[3]?.DefaultCaption,
        };
    });

    constructor() {
        effect(() => {
            const vs = this.vs();
            if (isSingleViewStateRecord(vs) || vs.PSTViewStateType === ViewStateTypes.PUBLICATION) {
                this.viewStateCustomCardItems.set(Util.deepCopy<FieldDef[]>((vs as StorytellingSingleViewStateRecord).PSTViewStateTopicCardCustomItems));
            }
        });
    }

    ngAfterViewInit(): void {
        this.containerResizeObserver = new ResizeObserver(entries => {
            const width = entries?.[0]?.contentRect?.width;
            if (width) {
                if (width < CARD_WIDTH_LOWER_BREAKPOINTS.medium) {
                    this.cardViewType.set(CardViewType.SMALL);
                } else if (width < CARD_WIDTH_LOWER_BREAKPOINTS.large) {
                    this.cardViewType.set(CardViewType.MEDIUM);
                } else {
                    this.cardViewType.set(CardViewType.LARGE);
                }
            }
        });

        this.containerResizeObserver?.observe(this.elmRef?.nativeElement);
    }

    ngOnDestroy(): void {
        this.containerResizeObserver?.unobserve(this.elmRef?.nativeElement);
        this.containerResizeObserver?.disconnect();
    }

    public pstFieldNames = PSTFieldNames;
    public viewStateTypes = ViewStateTypes;
    public CardViewType = CardViewType;

    private _topicPropsCache: FieldDef[] = [];

    get isSuperVisor(): boolean {
        return this.nomWebServicesJson?.HelloResults?.Supervisor;
    }

    private _topicPropsSimpleCache: LookupValue[] = [];
    public getList(): FieldDef[] {
        if (this._topicPropsCache && this._topicPropsCache.length > 0) return this._topicPropsCache;
        const generatedProps = [];
        const ret = this.globalTablesService.FieldDefs.filter(item => {
            if (item.ID === PSTFieldNames.TrashTime) return false;
            if (item.Validation.toUpperCase() === 'SPECIAL') return false;
            if (item.Validation.toUpperCase() === 'COST') return false;
            if (item.Validation.toUpperCase() === 'ADDRESS') {
                Util.fillAddressFields(item, generatedProps);
            }
            return item.TopicProp === true;
        });
        this._topicPropsCache = [...ret, ...generatedProps];
        return this._topicPropsCache;
    }

    public getCustomFieldsList(): LookupValue[] {
        if (this._topicPropsSimpleCache && this._topicPropsSimpleCache.length > 0) return this._topicPropsSimpleCache;
        const fields = this.getList();
        this._topicPropsSimpleCache = fields.map(item => ({ id: item.ID, title: item.DefaultCaption }));
        return this._topicPropsSimpleCache;
    }

    public selectionChanged(event: LookupValue, idx: number) {
        if (!event) {
            this.viewStateCustomCardItems()[idx] = null;
        }
        const field = this.getList().find(item => item?.ID === event?.id);
        if (!field) {
            this.viewStateCustomCardItems()[idx] = null;
        }
        this.viewStateCustomCardItems()[idx] = field;
        this.viewStateCustomCardItems.set([...this.viewStateCustomCardItems()]);
    }

    cancel() {
        this.dialogRef.close();
    }
    apply() {
        this.save();
    }

    save() {
        if (this.vs().PSTViewStateType === ViewStateTypes.TOPIC || this.vs().PSTViewStateType === ViewStateTypes.SEARCH || this.vs().PSTViewStateType === ViewStateTypes.PUBLICATION) {
            const fields: FieldDef[] = Util.deepCopy<FieldDef[]>(this.viewStateCustomCardItems());
            this.store.dispatch(ViewStateActions.updateViewStateCustomCardFields({ fields: fields }));
        }
        this.dialogRef.close(true);
    }

    async saveAsTeamDefault() {
        if (this.vs().PSTViewStateType === ViewStateTypes.TOPIC || this.vs().PSTViewStateType === ViewStateTypes.SEARCH || this.vs().PSTViewStateType === ViewStateTypes.PUBLICATION) {
            const fields: FieldDef[] = Util.deepCopy<FieldDef[]>(this.viewStateCustomCardItems());
            this.store.dispatch(ViewStateApiActions.applyTeamCustomCardFields({ fields: fields, team: this.navigationService.currentPlaybookTeam() }));
        }
        this.dialogRef.close(true);
    }

    async saveAsEnterpriseDefault() {
        if (this.vs().PSTViewStateType === ViewStateTypes.TOPIC || this.vs().PSTViewStateType === ViewStateTypes.SEARCH || this.vs().PSTViewStateType === ViewStateTypes.PUBLICATION) {
            const fields: FieldDef[] = Util.deepCopy<FieldDef[]>(this.viewStateCustomCardItems());
            this.store.dispatch(ViewStateApiActions.applyTeamCustomCardFields({ fields: fields, team: this.globalTablesService.EnterprisePlaybookGroup }));
        }
        this.dialogRef.close(true);
    }

    getInnerProp(item: FieldDef) {
        const val = this.data()[item.ID];
        if (val) return val[item.ComplexPropInnerId];
        return '';
    }

    compareFn(c1: FieldDef, c2: FieldDef): boolean {
        return c1 && c2 ? c1.ID === c2.ID : c1 === c2;
    }
}
